"use client";

import { useEffect, useRef, ReactNode } from "react";
import { Bot, User } from "lucide-react";
import { AgentMessage } from "@/lib/agent/mcp-client";
import { AgentDataCard } from "./AgentDataCard";

/**
 * Detects image URLs in text and renders them as <img> elements.
 * Supports common image extensions and CDN URLs.
 */
function renderContentWithMedia(content: string): ReactNode[] {
  // Match URLs ending in common image extensions or CDN URLs
  const imageUrlRegex = /(https?:\/\/[^\s)]+\.(?:jpg|jpeg|png|gif|webp)|https?:\/\/d2v7ifzl6y9m6d\.cloudfront\.net\/[^\s)]+)/gi;

  const parts = content.split(imageUrlRegex);

  return parts.map((part, idx) => {
    // Check if part is an image URL using non-stateful regex test
    const isImageUrl = /^https?:\/\/[^\s)]+\.(?:jpg|jpeg|png|gif|webp)$/i.test(part) ||
                       /^https?:\/\/d2v7ifzl6y9m6d\.cloudfront\.net\//i.test(part);

    if (isImageUrl) {
      return (
        <a
          key={idx}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-2"
        >
          <img
            src={part}
            alt="Generated image"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            className="rounded-lg max-w-full max-h-96 cursor-pointer hover:opacity-90 transition-opacity"
          />
        </a>
      );
    }
    return <span key={idx}>{part}</span>;
  });
}

interface AgentMessagesProps {
  messages: AgentMessage[];
  compact?: boolean; // For sidebar mode
}

export function AgentMessages({ messages, compact = false }: AgentMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 h-full overflow-y-auto">
        <div className="text-center max-w-md">
          <div className={`${compact ? 'w-12 h-12' : 'w-16 h-16'} rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4`}>
            <Bot className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-white`} />
          </div>
          <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-semibold text-white mb-2`}>
            Hi! I can help you manage your content.
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Try asking me about your streams, quotes, or videos:
          </p>
          <div className="space-y-2 text-sm text-gray-500">
            <p className="bg-gray-800/30 p-2 rounded border border-gray-700 text-left">
              "How many pending quotes do I have?"
            </p>
            <p className="bg-gray-800/30 p-2 rounded border border-gray-700 text-left">
              "Approve all pending quotes"
            </p>
            <p className="bg-gray-800/30 p-2 rounded border border-gray-700 text-left">
              "Generate videos for approved quotes"
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 h-full">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`flex gap-2 sm:gap-3 ${
            message.role === "user" ? "justify-end" : "justify-start"
          }`}
        >
          {message.role === "assistant" && (
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
          )}

          <div
            className={`max-w-[85%] ${
              message.role === "user"
                ? "bg-purple-600 text-white"
                : "bg-gray-800/50 text-gray-100 border border-gray-700"
            } rounded-2xl px-3 sm:px-4 py-2 sm:py-3`}
          >
            <div className="text-sm whitespace-pre-wrap break-words">
              {renderContentWithMedia(message.content)}
            </div>

            {/* Render data cards if present */}
            {message.data && (
              <div className="mt-3">
                <AgentDataCard
                  title={message.data.title || ""}
                  data={message.data.content || message.data}
                />
              </div>
            )}

            <p className="text-xs opacity-50 mt-2">
              {new Date(message.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>

          {message.role === "user" && (
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
          )}
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}
